import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { BCSLogo } from "@bcs-bv/design-system";
import styles from "./Sidebar.module.css";

/**
 * LOKALE FORK van de package-`Sidebar` (issues 357 en 41).
 *
 * WAAROM EEN FORK EN GEEN OMHULSEL. De drie dingen die we nodig hebben zitten
 * alle drie *binnen* het component: hoe een item gerenderd wordt (knop of link),
 * of een sectie een schakelaar heeft, en wat een schermlezer van de groepering
 * ziet. Van buitenaf is daar niets aan te doen — het package neemt een platte
 * `NavItem[]` en rendert die zelf.
 *
 * WAT ER ANDERS IS DAN UPSTREAM, en waarom:
 *
 *  1. **Inklapbare groepen** (357). Upstream heeft `section?: string`, maar dat
 *     tekent alleen een label: geen state, geen schakelaar, geen `aria-expanded`.
 *     Er is wél een `hasChildren`-vlag die een caret tekent en verder niets doet.
 *     Onze `group` + `groups` vervangen dat door iets dat werkt.
 *  2. **Nav-items zijn `<a href>`** (41). Upstream rendert `<button>`, dus
 *     ctrl- of middenklik opent geen tabblad en de statusbalk toont geen doel.
 *     Wij renderen een echte link en onderscheppen alleen de gewone linkerklik,
 *     zodat de router-guard blijft werken.
 *  3. **De groepering bestaat voor hulpsoftware.** Upstream zet het sectielabel
 *     op `aria-hidden="true"`; een schermlezer kreeg daardoor één platte rij
 *     knoppen — precies het probleem dat groeperen moet oplossen, maar dan voor
 *     wie het het hardst nodig heeft.
 *  4. **Geen Engels meer in de a11y-teksten.** Upstream hardcodeert
 *     `"${badge} notifications"` en `"Collapse sidebar"`. Dat is dezelfde
 *     familie als issue 419 en 643. Alles komt nu als prop binnen — de naam van
 *     het landmark (`"Main navigation"`) was daarbij blijven staan en volgde in
 *     issue 294.
 *
 * AURORA-CONTRACT DAT WE WÉL AANHOUDEN (spec v2.5.0 §8.4): breedtes 252/58px,
 * logo-vlak 56px, item 36px met 8px radius, sectielabel 11px/600 uppercase op
 * `var(--sidebar-section-label)`, en de ingeklapte ritmiek — elk item houdt zijn
 * Y-positie, labels verdwijnen met `opacity`/`width`, nooit met `display:none`.
 *
 * GEMELD BIJ HET DS-TEAM. Zie AURORA-NOTES.md; zodra upstream gegroepeerde
 * navigatie levert verhuist dit component terug naar het package.
 */

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  /** Upstream-pariteit: een puur visueel sectielabel zonder inklap-gedrag. */
  section?: string;
  /** Id van de groep waar dit item onder valt. Zonder groep staat het los bovenaan. */
  group?: string;
  /** Echte URL, zodat ctrl/midden-klik een tabblad opent (issue 41). */
  href?: string;
}

interface NavGroup {
  id: string;
  label: string;
  /** Staat de groep open als er nog niets onthouden is? Standaard: ja. */
  defaultOpen?: boolean;
}

interface SidebarProps {
  expanded?: boolean;
  items: NavItem[];
  activeItem?: string;
  onItemClick?: (id: string) => void;
  /**
   * De groepen, in rendervolgorde. Items verwijzen ernaar met `group`.
   * Leeg of weggelaten = de platte lijst van vroeger, ongewijzigd.
   */
  groups?: readonly NavGroup[];
  /** Sleutel waaronder de open/dicht-stand bewaard wordt. Leeg = niet onthouden. */
  groupStateKey?: string;
  onToggle?: (nextExpanded: boolean) => void;
  appName?: string;
  collapseLabel?: string;
  /**
   * De naam van het navigatie-landmark. LOKALE toevoeging (issue 294): upstream
   * zet hier vast `"Main navigation"`. Een prop en geen AuroraLabels-sleutel,
   * omdat een landmark-naam per exemplaar hoort te kunnen verschillen.
   */
  ariaLabel?: string;
  /** a11y-teksten — als prop zodat ze door de vertaallaag komen. */
  collapseAriaLabel?: string;
  expandAriaLabel?: string;
  badgeLabel?: (count: number) => string;
}

function renderAppName(appName: string) {
  const trimmed = appName.trim();
  const spaceIdx = trimmed.indexOf(" ");
  if (spaceIdx === -1) {
    return <span className={styles.logoBold}>{trimmed}</span>;
  }
  return (
    <>
      <span className={styles.logoBold}>{trimmed.slice(0, spaceIdx)}</span>
      <span className={styles.logoRegular}>{trimmed.slice(spaceIdx + 1)}</span>
    </>
  );
}

/**
 * Zet de items om in een rij blokken: losse items op hun eigen plek, en een groep
 * op de plek van zijn **eerste** item.
 *
 * DE VOLGORDE VAN DE AANROEPER BLIJFT STAAN, en dat is geen detail. In `AppShell`
 * staat de instellingen-hub bewust onderaan, met een comment erbij waarom. Zou
 * deze functie alle losse items vooraan zetten, dan verhuist die hub stilzwijgend
 * naar boven — een herordening die niemand heeft gevraagd en die je pas in de
 * browser ziet.
 *
 * Een groep die na filtering **één of geen** item overhoudt rendert plat. Een kop
 * met één item eronder is geen groepering maar een extra klik. Dat is niet
 * theoretisch: de nav wordt op rechten gefilterd, en een auditor houdt van de hele
 * beheerkant het logboek over.
 */
type Block =
  | { kind: "item"; item: NavItem }
  | { kind: "group"; group: NavGroup; items: NavItem[] };

function toBlocks(items: NavItem[], groups: readonly NavGroup[]): Block[] {
  const perGroup = new Map<string, NavItem[]>();
  for (const item of items) {
    if (!item.group) continue;
    if (!groups.some((g) => g.id === item.group)) continue;
    const bestaand = perGroup.get(item.group);
    if (bestaand) bestaand.push(item);
    else perGroup.set(item.group, [item]);
  }

  const blocks: Block[] = [];
  const gedaan = new Set<string>();
  for (const item of items) {
    const groepItems = item.group ? perGroup.get(item.group) : undefined;
    if (!groepItems) {
      blocks.push({ kind: "item", item });
      continue;
    }
    if (gedaan.has(item.group!)) continue;
    gedaan.add(item.group!);

    if (groepItems.length <= 1) {
      for (const los of groepItems) blocks.push({ kind: "item", item: los });
      continue;
    }
    const group = groups.find((g) => g.id === item.group)!;
    blocks.push({ kind: "group", group, items: groepItems });
  }
  return blocks;
}

/** Onder welke sleutel we bijhouden wélke groepen de gebruiker al eens gezien heeft. */
function seenKey(key: string): string {
  return `${key}.seen`;
}

/** De groepen die deze gebruiker al eens in beeld had; `null` als dat nooit is vastgelegd. */
function readSeenGroups(key: string | undefined): string[] | null {
  return readOpenState(key === undefined ? undefined : seenKey(key));
}

function readOpenState(key: string | undefined): string[] | null {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : null;
  } catch {
    // Private browsing, geblokkeerde site-data of onleesbare JSON. De nav hoort
    // dan gewoon te werken met de standaard-stand, niet leeg te blijven.
    return null;
  }
}

const Sidebar: React.FC<SidebarProps> = ({
  expanded = true,
  items,
  activeItem,
  onItemClick,
  groups = [],
  groupStateKey,
  onToggle,
  appName = "BCS Nova",
  collapseLabel = "Inklappen",
  ariaLabel = "Main navigation",
  collapseAriaLabel = "Inklappen",
  expandAriaLabel = "Uitklappen",
  badgeLabel,
}) => {
  const panelIdBase = useId();
  const blocks = useMemo(() => toBlocks(items, groups), [items, groups]);

  const [open, setOpen] = useState<ReadonlySet<string>>(() => {
    const bewaard = readOpenState(groupStateKey);
    const standaard = groups.filter((g) => g.defaultOpen !== false).map((g) => g.id);
    if (!bewaard) return new Set(standaard);

    // ⚠️ EEN GROEP DIE NOG NIET BESTOND VOLGT ZIJN EIGEN STANDAARD (issue 135). De bewaarde
    // stand is een lijst van wat ópen staat, dus een groep die er niet in voorkomt is niet te
    // onderscheiden van een groep die de gebruiker dichtklapte — tenzij je kijkt of hij
    // überhaupt al eens bewaard is. Zonder dit onderscheid komt elke nieuwe groep dicht
    // binnen bij iedereen die de app al eerder opende, en dat is precies wie het opvalt:
    // de catalogi van de omgeving verschijnen dan als een dichtgeklapt kopje.
    const gezien = new Set(readSeenGroups(groupStateKey) ?? bewaard);

    return new Set([...bewaard, ...standaard.filter((id) => !gezien.has(id))]);
  });

  // De groep van de huidige pagina gaat altijd open. Anders navigeer je naar een
  // scherm en zie je in het menu niet waar je bent — en dat is precies waar een
  // gebruiker naar kijkt om zich te oriënteren.
  const activeGroup = items.find((i) => i.id === activeItem)?.group;
  useEffect(() => {
    if (!activeGroup) return;
    setOpen((prev) => (prev.has(activeGroup) ? prev : new Set([...prev, activeGroup])));
  }, [activeGroup]);

  useEffect(() => {
    if (!groupStateKey) return;
    try {
      window.localStorage.setItem(groupStateKey, JSON.stringify([...open]));
      // Welke groepen we ooit getoond hebben, zodat een nieuwe groep straks zijn eigen
      // standaard volgt in plaats van "staat niet in de open-lijst, dus dicht".
      window.localStorage.setItem(seenKey(groupStateKey), JSON.stringify(groups.map((g) => g.id)));
    } catch {
      // Niet kritiek: de stand geldt dan voor deze sessie.
    }
  }, [open, groups, groupStateKey]);

  const toggleGroup = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const renderItem = useCallback(
    (item: NavItem) => {
      const isActive = item.id === activeItem;
      const className = [styles.navItem, isActive ? styles.active : ""].filter(Boolean).join(" ");
      const inhoud = (
        <>
          <span className={styles.navIcon} aria-hidden="true">
            {item.icon}
          </span>
          <span className={styles.navLabel}>{item.label}</span>
          {item.badge != null && item.badge > 0 && (
            <span className={styles.badge} aria-label={badgeLabel?.(item.badge)}>
              {item.badge}
            </span>
          )}
        </>
      );

      // Geen `key` in dit object: React wil hem rechtstreeks op het element zien en
      // waarschuwt bij een key die via een spread binnenkomt.
      const gedeeld = {
        className,
        "aria-current": isActive ? ("page" as const) : undefined,
        // Ingeklapt staat er alleen een icoon; dan draagt het item zelf de naam.
        "aria-label": expanded ? undefined : item.label,
        title: expanded ? undefined : item.label,
      };

      // Zonder href kan er geen echte link zijn — dan blijft het de knop van
      // vroeger. Zo is dit component bruikbaar voor nav die niet op een URL zit.
      if (!item.href) {
        return (
          <button key={item.id} type="button" {...gedeeld} onClick={() => onItemClick?.(item.id)}>
            {inhoud}
          </button>
        );
      }

      return (
        <a
          key={item.id}
          {...gedeeld}
          href={item.href}
          onClick={(e) => {
            // Alleen de gewone linkerklik onderscheppen. Ctrl/cmd/shift/alt en
            // middenklik laten we met rust: dat is precies wat issue 41 mist —
            // de browser mag dan zelf een tabblad of venster openen.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            onItemClick?.(item.id);
          }}
        >
          {inhoud}
        </a>
      );
    },
    [activeItem, expanded, onItemClick, badgeLabel],
  );

  const rootClass = [styles.sidebar, styles.relative, expanded ? styles.expanded : styles.collapsed].join(" ");
  const ToggleIcon = expanded ? CaretLeftIcon : CaretRightIcon;

  // Ingeklapt (rail) zijn er geen labels, dus ook geen groepskoppen — en een
  // dichtgeklapte groep zou items onbereikbaar maken zonder dat je ziet waarom.
  // Alles plat is daar de enige eerlijke vorm.
  const railMode = !expanded;

  return (
    <aside className={rootClass} aria-label={ariaLabel}>
      <div className={styles.logo}>
        <BCSLogo size={24} />
        <span className={styles.logoText}>{renderAppName(appName)}</span>
      </div>

      <nav className={styles.nav}>
        {railMode
          ? items.map(renderItem)
          : blocks.map((block) => {
            if (block.kind === "item") return renderItem(block.item);

            const { group, items: groupItems } = block;
            const isOpen = open.has(group.id);
            const panelId = `${panelIdBase}-${group.id}`;
            return (
              <React.Fragment key={group.id}>
                <h2 className={styles.sectionLabel}>
                  <button
                    type="button"
                    className={styles.groupHeader}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className={styles.sectionLabelText}>{group.label}</span>
                    <span
                      className={[styles.groupCaret, isOpen ? styles.groupCaretOpen : ""]
                        .filter(Boolean)
                        .join(" ")}
                      aria-hidden="true"
                    >
                      <CaretDownIcon size={14} weight="regular" />
                    </span>
                  </button>
                </h2>
                <div id={panelId} className={styles.groupPanel} hidden={!isOpen}>
                  {groupItems.map(renderItem)}
                </div>
              </React.Fragment>
            );
          })}
      </nav>

      {onToggle && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => onToggle(!expanded)}
          aria-label={expanded ? collapseAriaLabel : expandAriaLabel}
          aria-expanded={expanded}
          title={expanded ? undefined : expandAriaLabel}
        >
          <span className={styles.toggleIcon} aria-hidden="true">
            <ToggleIcon size={16} weight="regular" />
          </span>
          <span className={styles.toggleLabel}>{collapseLabel}</span>
        </button>
      )}
    </aside>
  );
};

export { Sidebar };
export type { SidebarProps, NavItem, NavGroup };
